import { defaultTo, get, omitBy, isNil } from 'lodash-es';
import { ClientConfig } from 'client/configuration';
import { getQuery } from 'client/utils/location';
import {
  getMultiOfferResultsLink,
  getOfferDetailsPageLink,
} from 'site-modules/shared/utils/multi-offer-link-constructor';
import { venomHistory } from 'client/utils/history/venom-history';
import { SOLICITATION_SUPPORTED_PARTNERS } from 'site-modules/shared/constants/multi-offer/multi-offer-config';
import { PARTNERS } from 'site-modules/shared/constants/multi-offer/partners';

/**
 * Returns if the given partner is prequalified for the Multi-Offer experience.
 *
 * @param {String} partner the partner to check.
 * @param {Object} multiOfferPrequalification the Prequalify API response.
 * @returns {Boolean}
 */
export const isPartnerPrequalified = (partner, multiOfferPrequalification) =>
  get(multiOfferPrequalification, ['partners', partner, 'solicitation', 'isPrequalified']) === true;

/**
 * Returns array of solicitation support partners that user prequalifies for the Multi-Offer experience.
 *
 * @param {Object} multiOfferPrequalification the Prequalify API response.
 * @returns {Array} prequalified partners
 */
export const getPrequalifiedPartners = multiOfferPrequalification =>
  SOLICITATION_SUPPORTED_PARTNERS.filter(partner => isPartnerPrequalified(partner, multiOfferPrequalification));

export const WS_EVENTS = {
  MESSAGE: 'message',
  INITIAL_PUSH: 'initialPushModsRecord',
  UPDATE_PUSH: 'updatePushModsRecord',
};
export const WS_CONFIG_MULTI_OFFER_PATH_KEY = 'multiOfferPath';

export const getMultiOfferSseConnectionPath = (pathKey, modsId) => {
  const { [pathKey]: path } = ClientConfig.get('edmundsMultiOfferWebSocket');

  if (!path || !modsId) {
    return null;
  }

  return `${path}/sse-connection/${modsId}/`;
};

/**
 * Get websocket configuration from client configuration.
 * DEV and SANDBOX environments is explicitly specified in the
 * environment config file, where other environments uses "location.host"
 * @param {String} pathKey key for the WebSocket path value
 * @returns {Object}
 */
export const getWebSocketConfig = pathKey => {
  const { host, [pathKey]: path } = ClientConfig.get('edmundsMultiOfferWebSocket');
  const webSocketHost = host || window.location.host;
  return {
    path,
    origin: `wss://${webSocketHost}`,
  };
};

export const transformPartnerAppraisalToModsRecord = ({ vehicle, dma, zipCode, abTestBucketing }) => ({
  seller: omitBy(
    {
      zipCode,
      dma: dma || null,
      abTestBucketing: abTestBucketing || [],
    },
    isNil
  ),
  vehicle,
});

export const getIsEmoSearchEnabled = location => getQuery(location).emoSearch === 'true';

export const hasPrequalifiedPartner = partners =>
  Object.values(defaultTo(partners, {})).some(({ solicitation: { isPrequalified } }) => isPrequalified);

export const hasMultiOfferPartners = partners => !!partners?.some(partner => partner !== PARTNERS.CARMAX);

/**
 * Redirects user to MOR page with given modsId
 * @param {String} modsId Mods Record I.D.
 * @param {Boolean} isAppExtensionPage for whether or not this is the webview version of the page
 * @param {Object} location
 */
export const goToMultiOfferResultsPage = ({ modsId, isAppExtensionPage, location }) => {
  const { promo } = getQuery(location);
  const page = getMultiOfferResultsLink({ modsId, isAppExtensionPage, queryParams: { promo } });
  venomHistory.push(page);
};

/**
 * Redirects user to ODP with given modsId
 * @param {String} modsId Mods Record I.D.
 * @param {Boolean} isAppExtensionPage for whether or not this is the webview version of the page
 * @param {Object} location
 */
export const goToOfferDetailsPage = ({ modsId, isAppExtensionPage, location }) => {
  const { promo } = getQuery(location);
  const page = getOfferDetailsPageLink({ modsId, isAppExtensionPage, queryParams: { promo } });
  venomHistory.push(page);
};
